/**
 * Paket tanımları — merkezin kendi ayarı.
 *
 * Sistemde hazır paket ya da önerilen fiyat YOK. Adı, seans sayısı,
 * fiyatı, para birimi, geçerlilik süresi: hepsini merkez belirliyor.
 * Bir merkez tek seans satar, bir başkası on iki haftalık program;
 * ikisi de aynı yapıyla çalışıyor.
 *
 * Paketi düzenlemek SATILMIŞ paketleri etkilemiyor: satış anında ad
 * ve fiyat kopyalanmıştı. "O zaman şu fiyata almıştık" iddiası
 * kayıtta duruyor.
 */

import { useCallback, useEffect, useState } from 'react'
import { BELGE_ADI, api, paraYaz, type BelgeAyarlari, type Paket } from '../api'

const BOS = { ad: '', seans: 8, fiyat: '', paraBirimi: 'TRY', gecerlilikGun: '' }

export function Paketler({ yazabilir }: { yazabilir: boolean }) {
  const [liste, setListe] = useState<Paket[] | null>(null)
  const [form, setForm] = useState({ ...BOS })
  const [formAcik, setFormAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const yukle = useCallback(async () => {
    try {
      const r = await api.paketler(true)
      setListe(r.paketler)
    } catch {
      setHata('Paketler alınamadı.')
      setListe([])
    }
  }, [])

  useEffect(() => {
    void yukle()
  }, [yukle])

  const ekle = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await api.paketEkle({
        ad: form.ad.trim(),
        seans: form.seans,
        fiyat: form.fiyat.replace(',', '.'),
        paraBirimi: form.paraBirimi.trim() || 'TRY',
        gecerlilikGun: form.gecerlilikGun ? Number(form.gecerlilikGun) : null,
      })
      setForm({ ...BOS })
      setFormAcik(false)
      await yukle()
    } catch {
      setHata('Paket eklenemedi. Fiyatı 1500 ya da 1500.00 gibi yazın.')
    } finally {
      setBekliyor(false)
    }
  }

  const degistir = async (p: Paket, g: Record<string, unknown>) => {
    try {
      await api.paketGuncelle(p.id, g)
      await yukle()
    } catch {
      setHata('Güncellenemedi.')
    }
  }

  return (
    <>
      <h1>Paketler</h1>
      <p className="alt">
        Merkezinizin sattığı hizmetler. Hazır paket ya da önerilen fiyat yok; hepsini siz
        belirliyorsunuz.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {yazabilir && (
        <div className="dugmeler" style={{ marginBottom: 14 }}>
          <button className="birincil" onClick={() => setFormAcik((v) => !v)}>
            {formAcik ? 'Vazgeç' : 'Paket ekle'}
          </button>
        </div>
      )}

      {formAcik && (
        <form className="kart" onSubmit={ekle}>
          <div className="satir">
            <label>
              Paket adı
              <input
                value={form.ad}
                onChange={(e) => setForm({ ...form, ad: e.target.value })}
                required
                placeholder="8 seanslık dikkat programı"
              />
            </label>
            <label>
              Seans sayısı
              <input
                type="number"
                min={1}
                max={200}
                value={form.seans}
                onChange={(e) => setForm({ ...form, seans: Number(e.target.value) })}
              />
            </label>
            <label>
              Fiyat
              <input
                value={form.fiyat}
                onChange={(e) => setForm({ ...form, fiyat: e.target.value })}
                required
                placeholder="12000"
              />
            </label>
            <label>
              Para birimi
              <input
                value={form.paraBirimi}
                onChange={(e) => setForm({ ...form, paraBirimi: e.target.value })}
                maxLength={8}
              />
            </label>
            <label>
              Geçerlilik (gün)
              <input
                type="number"
                min={1}
                max={3650}
                value={form.gecerlilikGun}
                onChange={(e) => setForm({ ...form, gecerlilikGun: e.target.value })}
                placeholder="boş = süresiz"
              />
            </label>
          </div>
          <div className="dugmeler">
            <button className="birincil" type="submit" disabled={bekliyor}>
              {bekliyor ? 'Ekleniyor…' : 'Ekle'}
            </button>
          </div>
        </form>
      )}

      {liste === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : liste.length === 0 ? (
        <div className="bilgi">
          Henüz paket tanımlı değil. Paket tanımlamadan da danışana doğrudan satış
          yapabilirsiniz; tanım, tekrar eden satışları kolaylaştırıyor.
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Paket</th>
              <th>Seans</th>
              <th>Fiyat</th>
              <th>Geçerlilik</th>
              <th>Durum</th>
              {yazabilir && <th />}
            </tr>
          </thead>
          <tbody>
            {liste.map((p) => (
              <tr key={p.id}>
                <td>
                  <strong>{p.ad}</strong>
                </td>
                <td>{p.seans}</td>
                <td>{paraYaz(p.fiyat, p.paraBirimi)}</td>
                <td className="alt" style={{ margin: 0 }}>
                  {p.gecerlilikGun ? `${p.gecerlilikGun} gün` : 'süresiz'}
                </td>
                <td>
                  <span className={'rozet' + (p.aktif ? ' ok' : ' warn')}>
                    {p.aktif ? 'Satışta' : 'Kapalı'}
                  </span>
                </td>
                {yazabilir && (
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => void degistir(p, { aktif: !p.aktif })}>
                      {p.aktif ? 'Satışı durdur' : 'Satışa aç'}
                    </button>
                    <button
                      style={{ marginLeft: 6 }}
                      onClick={() => void degistir(p, { arsiv: true })}
                    >
                      Arşivle
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="alt" style={{ marginTop: 16 }}>
        Paketi düzenlemek satılmış paketleri etkilemez: satış anındaki ad ve fiyat kayda
        kopyalanır.
      </p>

      <BelgeAyarlariBolumu yazabilir={yazabilir} />
    </>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Belge ayarları.
 *
 * Serbest meslek erbabı psikolog MAKBUZ keser; Ltd./A.Ş. çatısı
 * altındaki merkez fatura. Sistem hangisi olduğuna karar vermiyor.
 *
 * Oranlar da burada: KDV ve stopaj için kaynaklar çelişiyor ve stopaj
 * yalnızca ödeyen taraf kurumsa kesiliyor. Bu mali müşavirin kararı;
 * sistem oran önermiyor, girileni uyguluyor.
 */
function BelgeAyarlariBolumu({ yazabilir }: { yazabilir: boolean }) {
  const [a, setA] = useState<BelgeAyarlari | null>(null)
  const [bilgi, setBilgi] = useState<string | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  useEffect(() => {
    api
      .belgeAyarlari()
      .then((r) => setA(r.ayarlar))
      .catch(() => setA(null))
  }, [])

  if (!a) return null

  const kaydet = async () => {
    setHata(null)
    setBilgi(null)
    setBekliyor(true)
    try {
      await api.belgeAyarKaydet({
        belgeTuru: a.belgeTuru,
        unvan: a.unvan,
        vergiNo: a.vergiNo,
        vergiDairesi: a.vergiDairesi,
        adres: a.adres,
        kdvOrani: a.kdvOrani ? Number(a.kdvOrani) : null,
        stopajOrani: a.stopajOrani ? Number(a.stopajOrani) : null,
        hatirlatmaAcik: a.hatirlatmaAcik,
        hatirlatmaSaat: a.hatirlatmaSaat,
        odevHatirlatmaGun: a.odevHatirlatmaGun,
      } as never)
      setBilgi('Kaydedildi.')
    } catch {
      setHata('Kaydedilemedi.')
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <>
      <h2>Belge ayarları</h2>
      {hata && <div className="uyari">{hata}</div>}
      {bilgi && <div className="bilgi">{bilgi}</div>}

      <div className="kart">
        <div className="satir">
          <label>
            Kestiğiniz belge
            <select
              value={a.belgeTuru}
              disabled={!yazabilir}
              onChange={(e) => setA({ ...a, belgeTuru: e.target.value as never })}
            >
              <option value="smm">{BELGE_ADI['smm']} — serbest meslek erbabı</option>
              <option value="fatura">{BELGE_ADI['fatura']} — Ltd. / A.Ş.</option>
            </select>
          </label>
          <label>
            Unvan
            <input
              value={a.unvan ?? ''}
              disabled={!yazabilir}
              onChange={(e) => setA({ ...a, unvan: e.target.value })}
            />
          </label>
          <label>
            Vergi / TC no
            <input
              value={a.vergiNo ?? ''}
              disabled={!yazabilir}
              onChange={(e) => setA({ ...a, vergiNo: e.target.value })}
              maxLength={20}
            />
          </label>
          <label>
            Vergi dairesi
            <input
              value={a.vergiDairesi ?? ''}
              disabled={!yazabilir}
              onChange={(e) => setA({ ...a, vergiDairesi: e.target.value })}
            />
          </label>
          <label>
            Varsayılan KDV (%)
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={a.kdvOrani ?? ''}
              disabled={!yazabilir}
              onChange={(e) => setA({ ...a, kdvOrani: e.target.value })}
            />
          </label>
          {a.belgeTuru === 'smm' && (
            <label>
              Varsayılan stopaj (%)
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={a.stopajOrani ?? ''}
                disabled={!yazabilir}
                onChange={(e) => setA({ ...a, stopajOrani: e.target.value })}
              />
            </label>
          )}
        </div>
        <label>
          Adres
          <input
            value={a.adres ?? ''}
            disabled={!yazabilir}
            onChange={(e) => setA({ ...a, adres: e.target.value })}
            maxLength={400}
          />
        </label>
        {yazabilir && (
          <div className="dugmeler">
            <button className="birincil" disabled={bekliyor} onClick={() => void kaydet()}>
              {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        )}
        <p className="alt" style={{ margin: '10px 0 0' }}>
          Oranlar ve belge türü mali müşavirinizin kararıdır; sistem oran önermiyor. Stopaj
          yalnızca ödemeyi yapan taraf kurum ya da vergi mükellefi işletme olduğunda kesilir —
          bireysel danışanda sıfır bırakın.
        </p>
      </div>

      <h2>Hatırlatmalar</h2>
      <div className="kart">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={a.hatirlatmaAcik}
            disabled={!yazabilir}
            onChange={(e) => setA({ ...a, hatirlatmaAcik: e.target.checked })}
            style={{ width: 18 }}
          />
          Velilere randevu ve ev çalışması hatırlatması gönder
        </label>
        {a.hatirlatmaAcik && (
          <div className="satir">
            <label>
              Randevudan kaç saat önce
              <input
                type="number"
                min={1}
                max={168}
                value={a.hatirlatmaSaat}
                disabled={!yazabilir}
                onChange={(e) => setA({ ...a, hatirlatmaSaat: Number(e.target.value) })}
              />
            </label>
            <label>
              Ödev son tarihinden kaç gün önce
              <input
                type="number"
                min={0}
                max={14}
                value={a.odevHatirlatmaGun}
                disabled={!yazabilir}
                onChange={(e) => setA({ ...a, odevHatirlatmaGun: Number(e.target.value) })}
              />
            </label>
          </div>
        )}
        {yazabilir && (
          <div className="dugmeler">
            <button className="birincil" disabled={bekliyor} onClick={() => void kaydet()}>
              {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        )}
        <p className="alt" style={{ margin: '10px 0 0' }}>
          Hatırlatma yalnızca e-postası kayıtlı ilgili kişilere gider ve metinde{' '}
          <strong>çocuğun adı geçmez</strong>: aynı e-posta kutusunu başkası görebilir. Ödev
          hatırlatmasını kapatmak için gün sayısını 0 yapın. SMS sağlayıcısı bağlandığında aynı
          ayar SMS'e de uygulanacak.
        </p>
      </div>
    </>
  )
}
